/**
 * Relay storefront collector - PartCatalog (Shopify).
 *
 * Deploy: copy to the theme as assets/relay.js and load it early in
 * layout/theme.liquid:
 *
 *   <script src="{{ 'relay.js' | asset_url }}" defer></script>
 *
 * ---------------------------------------------------------------------------
 * Why the anonymous id is generated HERE and not by the server
 * ---------------------------------------------------------------------------
 * Relay lives on relay.partcatalog.com, same site as the storefront, so its
 * requests are first-party - but the id is still minted client-side and stored
 * in first-party storage on partcatalog.com (this predates the move from
 * autopartgear.com, and keeps tracking independent of response cookies),
 * then sent explicitly with every request. Relay never sets a tracking cookie.
 *
 * The cookie is written on the registrable domain (.partcatalog.com) so the id
 * is shared with shopify.partcatalog.com, and mirrored into localStorage so it
 * survives cookie clearing in either direction.
 * ---------------------------------------------------------------------------
 */
(function (window, document) {
  'use strict';

  if (window.Relay && window.Relay.__loaded) { return; }

  var ENDPOINT   = 'https://relay.partcatalog.com/public';
  var UID_KEY    = 'relay_uid';
  var SESSION_KEY = 'relay_sid';
  var COOKIE     = 'relay_uid';
  var COOKIE_DAYS = 400;
  var FLUSH_MS   = 1500;
  var MAX_BATCH  = 25;
  var ATTR_KEY   = 'relay_attr';
  var ATTR_DAYS  = 5;            // must match config attribution_window_days
  var STAMP_KEY  = 'relay_stamped';

  // -------------------------------------------------------------------------
  // Storage helpers - every one of these fails soft. Analytics must never be
  // able to break the storefront (private mode, disabled storage, quota).
  // -------------------------------------------------------------------------
  function lsGet(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v); } catch (e) {} }
  function ssGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) {} }

  function cookieGet(name) {
    var m = document.cookie.match(new RegExp('(?:^|;)\\s*' + name + '=([^;]+)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function cookieSet(name, value, days) {
    // Registrable domain so shopify.partcatalog.com sees the same id.
    var domain = '';
    var host = location.hostname;
    var m = host.match(/([^.]+\.[^.]+)$/);
    if (m && host !== 'localhost') { domain = '; domain=.' + m[1]; }
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; Max-Age=' + (days * 86400) + '; Path=/' + domain + '; SameSite=Lax' +
      (location.protocol === 'https:' ? '; Secure' : '');
  }

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) { return window.crypto.randomUUID(); }
    if (window.crypto && window.crypto.getRandomValues) {
      var b = new Uint8Array(16);
      window.crypto.getRandomValues(b);
      b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
      var s = '';
      for (var i = 0; i < 16; i++) { s += ('0' + b[i].toString(16)).slice(-2); }
      return s;
    }
    return 'r' + Date.now().toString(16) + Math.random().toString(16).slice(2, 14);
  }

  // -------------------------------------------------------------------------
  // Identity
  // -------------------------------------------------------------------------
  function getUid() {
    var uid = lsGet(UID_KEY) || cookieGet(COOKIE);
    if (!uid || uid.length < 16) { uid = uuid().replace(/-/g, ''); }
    lsSet(UID_KEY, uid);
    cookieSet(COOKIE, uid, COOKIE_DAYS);
    return uid;
  }

  function getSid() {
    var sid = ssGet(SESSION_KEY);
    if (!sid) { sid = uuid().replace(/-/g, '').slice(0, 24); ssSet(SESSION_KEY, sid); }
    return sid;
  }

  // -------------------------------------------------------------------------
  // Vehicle - read from the theme's existing YMM storage rather than keeping a
  // second copy. fitment-checker.js writes pc_ymm_v1; the collection page also
  // maintains a pc_ymm cookie.
  // -------------------------------------------------------------------------
  function currentVehicle() {
    var sources = [lsGet('pc_ymm_v1'), lsGet('ymm_selection'), cookieGet('pc_ymm')];
    for (var i = 0; i < sources.length; i++) {
      if (!sources[i]) { continue; }
      try {
        var v = JSON.parse(sources[i]);
        if (v && (v.year || v.make || v.model)) {
          return {
            year: v.year || null,
            make: v.make || null,
            model: v.model || null,
            submodel: v.submodel || v.trim || null,
            engine: v.engine || null,
            vin: v.vin || null
          };
        }
      } catch (e) { /* malformed - ignore */ }
    }
    return null;
  }

  // -------------------------------------------------------------------------
  // Page context
  //
  // collection_id and the product handle are what let Relay render REAL fitting
  // parts inside an email instead of a generic link, so they are picked up
  // automatically on every event.
  //
  // Add to the theme so these are always available:
  //   collection.liquid : <meta name="relay:collection-id" content="{{ collection.id }}">
  //   product.liquid    : <meta name="relay:product-handle" content="{{ product.handle }}">
  // The URL fallback below covers templates where the meta tag is missing.
  // -------------------------------------------------------------------------
  function meta(name) {
    var el = document.querySelector('meta[name="' + name + '"]');
    return el ? el.getAttribute('content') : null;
  }

  function pageContext() {
    var ctx = {};

    var collectionId = meta('relay:collection-id');
    if (collectionId) { ctx.collection_id = collectionId; }

    var handle = meta('relay:product-handle');
    if (!handle) {
      var m = location.pathname.match(/\/products\/([a-z0-9\-]+)/i);
      if (m) { handle = m[1]; }
    }
    if (handle) { ctx.product_handle = handle; }

    var sku = meta('relay:sku');
    if (sku) { ctx.sku = sku; }

    var brand = meta('relay:brand');
    if (brand) { ctx.brand = brand; }

    return ctx;
  }

  // -------------------------------------------------------------------------
  // Attribution
  //
  // Checkout runs on Shopify's domain, where neither our JS nor any cookie we
  // set survives. The only reliable bridge is Shopify CART ATTRIBUTES: what we
  // write here comes back to us in the order's note_attributes via webhook.
  //
  // TWO separate things ride that bridge, and they are not equally optional:
  //
  //   relay_uid  - the browsing anon id, stamped on EVERY cart. This is what
  //                lets shopify_webhook.php call Identity::resolve() with an
  //                anon id and create the relay_identities row that joins an
  //                order back to what the shopper did on site. Without it the
  //                dashboard's cohort queries (which resolve anon -> profile
  //                through that table) drop the shopper entirely, so any
  //                surface that is not the guarantee modal - the AI search
  //                bar, for one - measures as if it never converted anyone.
  //   relay_attr - the email send id, only present when a campaign link
  //                brought them here.
  //
  // 1. An email link lands with ?pc_m=<send id>  (added by Mailer::rewriteLinks)
  // 2. Store it first-party, with an expiry matching the attribution window
  // 3. Push it into the cart so it survives into checkout
  // -------------------------------------------------------------------------
  function captureAttribution() {
    var params = new URLSearchParams(location.search);
    var sendId = params.get('pc_m');
    if (!sendId) { return; }

    try {
      lsSet(ATTR_KEY, JSON.stringify({
        m: sendId,
        c: params.get('pc_c') || null,
        f: params.get('pc_f') || null,
        exp: Date.now() + (ATTR_DAYS * 86400000)
      }));
    } catch (e) { /* storage unavailable - attribution degrades to email match */ }

    // Unthrottled: the attribution is new and has to land now, even if this
    // cart was already stamped earlier in the session. Claiming the session
    // key keeps boot()'s stampCartOnce() from posting a second, identical
    // update on this same page load.
    ssSet(STAMP_KEY, cookieGet('cart') || '-');
    stampCart();
  }

  function currentAttribution() {
    var raw = lsGet(ATTR_KEY);
    if (!raw) { return null; }
    try {
      var attr = JSON.parse(raw);
      if (!attr || !attr.m || (attr.exp && Date.now() > attr.exp)) { return null; }
      return attr;
    } catch (e) { return null; }
  }

  /**
   * Write the identity (and, when we have one, the attribution) onto the
   * Shopify cart. Safe to call repeatedly: /cart/update.js merges attributes
   * rather than replacing the cart.
   */
  function stampCart() {
    if (!trackingAllowed()) { return; }   // no cart stamping without consent
    // Always present. pc-guarantee-modal.liquid writes the same key from the
    // same localStorage slot, so the two paths agree on the value and the
    // later of the two is a harmless no-op rewrite.
    var attributes = { relay_uid: getUid() };

    var attr = currentAttribution();
    if (attr) { attributes.relay_attr = String(attr.m); }

    // Carry the vehicle through too, so an order can be tied to the car it was
    // bought for even when the buyer never gave us one directly.
    var vehicle = currentVehicle();
    if (vehicle && vehicle.make) {
      attributes.relay_vehicle_year  = String(vehicle.year || '');
      attributes.relay_vehicle_make  = String(vehicle.make || '');
      attributes.relay_vehicle_model = String(vehicle.model || '');
    }

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attributes }),
      keepalive: true
    }).catch(function () { /* not on a Shopify page, or cart unavailable */ });
  }

  /**
   * Page-load stamp, throttled to once per cart per session so browsing does
   * not fire a POST per page view.
   *
   * The key is the Shopify cart token. On a first visit there is no cart yet,
   * so the first stamp lands on an empty cart, Shopify mints a token, and the
   * next page view sees a changed key and stamps the real cart once. After
   * that the key is stable and this is free. Keying on the token (rather than
   * a bare boolean) also means a cart that gets replaced mid-session - expired
   * cookie, cleared cart - is re-stamped instead of silently losing the id.
   */
  function stampCartOnce() {
    var key = cookieGet('cart') || '-';
    if (ssGet(STAMP_KEY) === key) { return; }
    ssSet(STAMP_KEY, key);
    stampCart();
  }

  // -------------------------------------------------------------------------
  // Queue
  // -------------------------------------------------------------------------
  var queue = [];
  var timer = null;

  // -------------------------------------------------------------------------
  // Consent
  //
  // Nothing is sent while the visitor has Global Privacy Control switched on,
  // or while Shopify's Customer Privacy API says analytics processing is not
  // allowed (a declined cookie banner, or a region that requires opt-in). The
  // API loads asynchronously, so events queue until the answer is in; a 3s
  // fallback decides "allowed" when the API never answers, and the decision
  // is redone when the banner fires visitorConsentCollected. Relay.identify()
  // is not gated: it only ever runs from a form the shopper submitted with
  // its own consent wording.
  // -------------------------------------------------------------------------
  var consent = { decided: false, allowed: true, waiting: [] };

  function gpcOn() {
    var v = navigator.globalPrivacyControl;
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  function trackingAllowed() { return consent.decided && consent.allowed; }

  function whenDecided(fn) {
    if (consent.decided) { fn(); } else { consent.waiting.push(fn); }
  }

  function settleConsent(allowed) {
    consent.allowed = !!allowed;
    consent.decided = true;
    if (!consent.allowed) { queue.length = 0; }
    var fns = consent.waiting.splice(0);
    fns.forEach(function (fn) { try { fn(); } catch (e) {} });
    if (consent.allowed && queue.length) { flush(false); }
  }

  function decideConsent() {
    if (gpcOn()) { settleConsent(false); return; }
    var settled = false;
    var apply = function () {
      if (settled) { return; }
      settled = true;
      var allowed = true;
      try {
        var cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.analyticsProcessingAllowed === 'function') {
          allowed = cp.analyticsProcessingAllowed() !== false;
        }
      } catch (e) { /* treat an API error as "no answer" */ }
      settleConsent(allowed);
    };
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { apply(); });
        setTimeout(apply, 3000);
        return;
      }
    } catch (e) { /* fall through */ }
    apply();
  }

  // The banner (or a preference change) reports a new answer: re-decide, and
  // drop anything queued meanwhile if the answer is no.
  document.addEventListener('visitorConsentCollected', function () {
    consent.decided = false;
    decideConsent();
  });

  // -------------------------------------------------------------------------
  // URL hygiene
  //
  // Account URLs carry live credentials - /account/reset/<id>/<token> and
  // /account/activate/<id>/<token> work until they are used - and order status
  // and checkout URLs carry access keys. Those paths are cut back to their
  // first segment(s) and lose their query; every other URL loses known secret
  // parameters and its fragment.
  // -------------------------------------------------------------------------
  var PRIVATE_PATH  = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?(account|customers|checkouts?|orders|cart\/c)(\/|$)/i;
  var PRIVATE_PARAM = /^(key|token|_ab|signature|hmac|code|state|password|customer_access_token|return_url|checkout_url)$/i;

  function safeUrl(raw) {
    if (!raw) { return null; }
    var u;
    try { u = new URL(String(raw), location.href); } catch (e) { return null; }
    if (PRIVATE_PATH.test(u.pathname)) {
      var segs = u.pathname.split('/').filter(Boolean);
      var keep = (segs[0] === 'account' || segs[1] === 'account') ? 2 : 1;
      if (/^[a-z]{2}(?:-[a-z]{2})?$/i.test(segs[0] || '') && segs.length > 1) { keep += 1; }
      return u.origin + '/' + segs.slice(0, keep).join('/');
    }
    var drop = [];
    u.searchParams.forEach(function (v, k) { if (PRIVATE_PARAM.test(k)) { drop.push(k); } });
    drop.forEach(function (k) { u.searchParams.delete(k); });
    u.hash = '';
    return u.toString();
  }

  function post(path, body, useBeacon) {
    var url = ENDPOINT + '/' + path;
    var payload = JSON.stringify(body);

    if (useBeacon && navigator.sendBeacon) {
      try {
        // text/plain avoids a CORS preflight on unload, when there is no time
        // for one. The endpoint parses the raw body regardless of content type.
        var blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(url, blob)) { return Promise.resolve(null); }
      } catch (e) { /* fall through to fetch */ }
    }

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
      credentials: 'omit',
      mode: 'cors'
    }).then(function (r) { return r.json().catch(function () { return null; }); })
      .catch(function () { return null; });
  }

  function flush(useBeacon) {
    if (!trackingAllowed()) { return Promise.resolve(null); }   // undecided: keep queued; denied: queue is empty
    if (!queue.length) { return Promise.resolve(null); }
    var batch = queue.splice(0, MAX_BATCH);
    if (timer) { clearTimeout(timer); timer = null; }

    return post('collect.php', {
      uid: getUid(),
      session_id: getSid(),
      vehicle: currentVehicle(),
      timezone: (Intl && Intl.DateTimeFormat) ? Intl.DateTimeFormat().resolvedOptions().timeZone : null,
      events: batch
    }, useBeacon);
  }

  function schedule() {
    if (timer) { return; }
    timer = setTimeout(function () { timer = null; flush(false); }, FLUSH_MS);
  }

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------
  var Relay = {
    __loaded: true,

    uid: getUid,

    /** Relay.track('product_view', {sku: 'AD1212'}) */
    track: function (name, props) {
      if (!name) { return; }
      if (consent.decided && !consent.allowed) { return; }
      props = props || {};
      var page = pageContext();
      // Explicit props win over auto-detected page context.
      if (page.product_handle && !props.product_handle) { props.product_handle = page.product_handle; }

      queue.push({
        name: name,
        occurred_at: Date.now(),
        url: safeUrl(location.href),
        referrer: safeUrl(document.referrer),
        session_id: getSid(),
        sku: props.sku || page.sku || null,
        part_type: props.part_type || null,
        brand: props.brand || page.brand || null,
        collection_id: props.collection_id || page.collection_id || null,
        value: props.value || null,
        props: props
      });
      if (queue.length >= MAX_BATCH) { flush(false); } else { schedule(); }
    },

    /**
     * Capture an email WITH consent.
     *
     * Relay.identify('a@b.com', {
     *   form_id: 'part_sheet_pdp',
     *   consent: true,
     *   consent_text: document.getElementById('relay-consent-text').textContent,
     *   event_name: 'part_sheet_requested',
     *   sku: 'AD1212'
     * })
     *
     * consent_text must be the exact wording rendered on screen. The server
     * rejects consent:true without it - deliberately.
     */
    identify: function (email, opts) {
      opts = opts || {};
      if (!email) { return Promise.resolve(null); }
      return post('identify.php', {
        uid: getUid(),
        email: email,
        form_id: opts.form_id || null,
        consent: !!opts.consent,
        consent_text: opts.consent_text || null,
        source_url: safeUrl(location.href),
        vehicle: opts.vehicle || currentVehicle(),
        sku: opts.sku || null,
        part_type: opts.part_type || null,
        brand: opts.brand || null,
        event_name: opts.event_name || 'email_captured',
        first_name: opts.first_name || null,
        last_name: opts.last_name || null,
        website: opts.honeypot || ''
      }, false);
    },

    /** Explicitly record a vehicle (VIN decoder, garage UI). */
    setVehicle: function (vehicle, source) {
      if (!vehicle) { return; }
      if (consent.decided && !consent.allowed) { return; }
      queue.push({
        name: 'vehicle_added',
        occurred_at: Date.now(),
        url: safeUrl(location.href),
        session_id: getSid(),
        vehicle: Object.assign({}, vehicle, { source: source || 'ymm' }),
        props: { source: source || 'ymm' }
      });
      flush(false);
    },

    flush: function (useBeacon) { return flush(!!useBeacon); },

    /** Re-stamp the cart. Call after add-to-cart if your theme replaces the cart. */
    stampCart: stampCart,
    stampCartOnce: stampCartOnce,

    /** Current attribution, for debugging. */
    attribution: currentAttribution
  };

  window.Relay = Relay;

  // -------------------------------------------------------------------------
  // Automatic instrumentation
  // -------------------------------------------------------------------------
  function autoPageView() {
    var props = {};
    var body = document.body;
    if (body && body.className.indexOf('template-product') > -1) { props.template = 'product'; }
    Relay.track('page_view', props);

    // A product page also gets a first-class product_view carrying the SKU
    // (from the relay:sku meta tag) - segments and flows key off product_view.
    var page = pageContext();
    if (page.sku) { Relay.track('product_view', { sku: page.sku }); }
  }

  function boot() {
    decideConsent();
    autoPageView();   // queued; sent only once consent is decided and allowed
    whenDecided(function () {
      if (!consent.allowed) { return; }
      captureAttribution();
      // captureAttribution() only stamps when a campaign link brought the
      // shopper here; relay_uid has to be on the cart for everyone (who allows
      // tracking), not just email traffic.
      stampCartOnce();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-stamp when the cart changes - some themes rebuild the cart object and
  // drop attributes on add-to-cart. NOTE: nothing in this theme currently
  // dispatches cart:updated (pc-topnav and main-cart-items only listen for
  // it), so this is a hook for whenever something does, not live coverage.
  // stampCartOnce() on each page view is what actually keeps the id on the
  // cart today.
  document.addEventListener('cart:updated', stampCart);

  // The theme already dispatches these; piggyback rather than re-implement.
  document.addEventListener('vehicle:changed', function (e) {
    var v = e && e.detail;
    if (v && v.year && v.make && v.model) {
      Relay.track('vehicle_changed', { year: v.year, make: v.make, model: v.model });
    }
  });
  document.addEventListener('vehicle:cleared', function () {
    Relay.track('vehicle_cleared', {});
  });

  // Flush on the way out. visibilitychange is the only event that fires
  // reliably on mobile Safari; pagehide covers bfcache navigations.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { flush(true); }
  });
  window.addEventListener('pagehide', function () { flush(true); });

})(window, document);
